import { useShallow } from 'zustand/react/shallow'

import { Destination } from '@ui/__generated__/graphql'
import { SliceStatusToggles } from '@ui/components/core'
import { ApolloErrorMessage } from '@ui/components/core/ApolloErrorMessage'
import { CardItem, CardStack } from '@ui/components/core/CardStack'
import { Spinner } from '@ui/components/loaders'
import { NodeTypeEnum, PanelEnum } from '@ui/interfaces'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { GetDestinationsQuery } from '@ui/loaders/destinations'
import { useExporterNodeStore, useNodeAndEdgeStore, useSaveUpdateStatusStore } from '@ui/stores'

export function DestinationWizard() {
  const { data, loading, error } = GetDestinationsQuery()

  const logoPathBySlug = useLogoPathBySlug(
    data?.destinations.map((destination: Destination) => destination.template.slug)
  )

  const currentNodeId = useNodeAndEdgeStore(useShallow((state) => state.currentNodeId)) as string
  const setCurrentNodeId = useNodeAndEdgeStore(useShallow((state) => state.setCurrentNodeId))

  const nodes = useNodeAndEdgeStore.getState().nodes
  const currentNode = nodes.find((node) => node.id === currentNodeId)

  const setTemplate = useExporterNodeStore(useShallow((state) => state.setTemplate))
  const setPipelineModified = useSaveUpdateStatusStore(useShallow((state) => state.setPipelineModified))

  const updateNodeKeyValue = useNodeAndEdgeStore(useShallow((state) => state.updateNodeKeyValue))

  const updateNodeType = useNodeAndEdgeStore(useShallow((state) => state.updateNodeType))

  const setDisplayModal = useNodeAndEdgeStore(useShallow((state) => state.setDisplayModal))

  const saveToConfigAndClose = async (destination: Destination) => {
    const {
      id,
      name,
      template: { slug },
    } = destination

    updateNodeType(currentNodeId, NodeTypeEnum.EXPORT)
    updateNodeKeyValue(currentNodeId, {
      modified: true,
      exportType: slug || '',
      description: name || '',
      destinationId: id,
    })
    setPipelineModified(true)
    setDisplayModal(PanelEnum.HIDDEN)
    setCurrentNodeId(null)
    setTemplate(null)
  }

  if (error) {
    return <ApolloErrorMessage error={error} />
  }

  const destinations: Destination[] = data?.destinations || []

  return (
    <div>
      <div className="mb-6 border-b border-gray-200 pb-5 pr-4 sm:pr-6">
        <div className="mb-6 text-2xl font-medium leading-6 text-gray-900">Select a destination</div>
        <SliceStatusToggles />
      </div>
      <div className="overflow-hidden">
        {loading ? (
          <Spinner />
        ) : (
          <>
            <CardStack
              items={destinations.map((destination) => ({
                ...destination,
                subtitle: destination.template.name || '',
              }))}
              onSelect={saveToConfigAndClose}
              isSelected={(destination) => currentNode?.data.destinationId === destination.id}
              getImage={(destination) => {
                const slug = destination.template.slug || ''
                const logoPath = logoPathBySlug[slug]

                return {
                  src: logoPath,
                  alt: slug,
                }
              }}
            />
            <CardItem
              onClick={() => {
                setDisplayModal(PanelEnum.EXPORT)
              }}
              name="Create new destination"
            />
          </>
        )}
      </div>
    </div>
  )
}
